import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SketchPicker } from 'react-color';
import { Upload, DatePicker, Spin } from 'antd';
import {
  PlusOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  ArrowDownTrayIcon,
  PaintBrushIcon,
  PhotoIcon,
  RectangleStackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import storeApi from '../../../../services/storeApi';
import bannerApi from '../../../../services/bannerApi';
import DialogShell from '../components/dialog-shell';
import {
  SectionHeading,
  FieldLabel,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/settings-ui';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

const BrandingTab = ({ storeDetail, checkUpdate }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [loading, setLoading] = useState({
    upsert: false,
    banner: false,
  });
  const [form, setForm] = useState({
    terminallogo: storeDetail.terminallogo,
    sitecolor: storeDetail.sitecolor || '#ff5f5f',
  });
  const [bannerForm, setBannerForm] = useState({
    storeid: storeDetail.id,
    displayorder: 0,
    image: '',
  });
  const [bannerList, setBannerList] = useState([]);
  const [dialogs, setDialogs] = useState({ bannerUpload: false });
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setDisplayColorPicker(false);
      }
    };
    if (displayColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [displayColorPicker]);

  const handleChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleBannerFormChange = (val, key) => {
    setBannerForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickSubmit = async () => {
    try {
      setLoading((prev) => ({ ...prev, upsert: true }));
      const body = {
        ...storeDetail,
        ...form,
        websiteclosedat: storeDetail.websiteclosedat || '',
      };
      const formData = new FormData();

      for (const key of Object.keys(body)) {
        if (
          key === 'terminallogo' &&
          body.terminallogo !== '' &&
          body.terminallogo !== storeDetail.terminallogo
        ) {
          if (body.terminallogoid) {
            await storeApi.deleteTerminalLogoById(body.id);
          }
          formData.append('terminallogo', body.terminallogo);
        } else if (body[key] && body[key] !== 'null') {
          formData.append(key, body[key]);
        }
      }

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(formData, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false);
    } finally {
      setDisplayColorPicker(false);
      setLoading((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setDialogs((prev) => ({ ...prev, bannerUpload: false }));
  };

  const onClickUploadBanner = async () => {
    try {
      setLoading((prev) => ({ ...prev, banner: true }));
      const formData = new FormData();
      for (const key of Object.keys(bannerForm)) {
        formData.append(key, bannerForm[key]);
      }
      const res = await bannerApi.upsert(formData);
      if (res.status === 200) {
        checkUpdate(true);
      }
      getBannerList();
      onCloseUpsert();
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, banner: false }));
    }
  };

  const getBannerList = async () => {
    try {
      setLoading((prev) => ({ ...prev, banner: true }));
      const query = `?storeid=${storeDetail.id}&isdeleted=false`;
      const res = await bannerApi.list(query);
      setBannerList(res.data.data || []);
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, banner: false }));
    }
  };

  const onChangeBannerForm = (dateRange) => {
    setBannerForm((prev) => ({
      ...prev,
      startdate: dateRange?.[0],
      enddate: dateRange?.[1],
    }));
  };

  const onClickDeleteBanner = async (bannerId) => {
    try {
      setLoading((prev) => ({ ...prev, banner: true }));
      const res = await bannerApi.deleteById(bannerId);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
    } finally {
      getBannerList();
      setLoading((prev) => ({ ...prev, banner: false }));
    }
  };

  const uploadButton = (
    <div className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400">
      {loading.upsert ? (
        <LoadingOutlined className="text-lg" />
      ) : (
        <PlusOutlined className="text-lg" />
      )}
      <span className="mt-2 text-xs font-medium">Upload</span>
    </div>
  );

  useEffect(() => {
    getBannerList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Branding"
        description="Customize the look of this store's kiosk and customer-facing pages."
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <PaintBrushIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Kiosk color
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                The accent color used across kiosk screens.
              </p>
              <div className="relative mt-4" ref={pickerRef}>
                <button
                  type="button"
                  onClick={() => setDisplayColorPicker((v) => !v)}
                  className="inline-flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-slate-600"
                >
                  <span
                    className="h-5 w-5 rounded-full ring-2 ring-white shadow dark:ring-slate-800"
                    style={{ background: form.sitecolor }}
                  />
                  <span className="font-mono text-xs uppercase tracking-wider">
                    {form.sitecolor}
                  </span>
                </button>
                {displayColorPicker && (
                  <div className="absolute left-0 top-full z-20 mt-2">
                    <SketchPicker
                      color={form.sitecolor}
                      onChange={(e) => handleChange(e.hex, 'sitecolor')}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <PhotoIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Kiosk image
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Background shown on the kiosk welcome screen.
              </p>
              <div className="mt-4">
                <Upload
                  multiple={false}
                  maxCount={1}
                  name="avatar"
                  onChange={(e) => handleChange(e.file, 'terminallogo')}
                  listType="picture-card"
                  className="avatar-uploader"
                  beforeUpload={() => false}
                >
                  {storeDetail.terminallogo ? (
                    <img
                      src={storeDetail.terminallogo}
                      alt="Terminal"
                      style={{ width: '100%' }}
                    />
                  ) : (
                    uploadButton
                  )}
                </Upload>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <RectangleStackIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Banner images
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Schedule promotional banners to display on the kiosk during a
                date range.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDialogs((p) => ({ ...p, bannerUpload: true }))}
            className={secondaryButtonClass + ' w-full sm:w-auto'}
          >
            <UploadOutlined />
            Upload banner
          </button>
        </div>

        <div className="mt-5">
          {loading.banner ? (
            <div className="flex items-center justify-center py-10">
              <Spin />
            </div>
          ) : bannerList.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center dark:border-slate-700 dark:bg-slate-900/30">
              <PhotoIcon
                className="h-10 w-10 text-slate-300 dark:text-slate-600"
                aria-hidden="true"
              />
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                No banners scheduled yet.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {bannerList.map((banner) => (
                <li
                  key={banner.id}
                  className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <img
                    className="h-14 w-24 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                    src={banner.imagepath}
                    alt=""
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                      Scheduled
                    </p>
                    <p className="mt-0.5 text-sm font-medium text-slate-800 tabular-nums dark:text-slate-200">
                      {moment
                        .utc(banner.startdate)
                        .local()
                        .format('MMM D, YYYY')}
                      {' — '}
                      {moment.utc(banner.enddate).local().format('MMM D, YYYY')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onClickDeleteBanner(banner.id)}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                    aria-label="Delete banner"
                  >
                    <TrashIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onClickSubmit}
          disabled={loading.upsert}
          className={primaryButtonClass}
        >
          {loading.upsert ? 'Saving…' : 'Save branding'}
        </button>
      </div>

      <DialogShell
        show={dialogs.bannerUpload}
        onClose={onCloseUpsert}
        title="Schedule a banner"
        description="Upload an image and choose when it should appear on the kiosk."
        footer={
          <>
            <button
              type="button"
              onClick={onCloseUpsert}
              className={secondaryButtonClass}
              disabled={loading.banner}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onClickUploadBanner}
              className={primaryButtonClass}
              disabled={loading.banner}
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              {loading.banner ? 'Uploading…' : 'Upload banner'}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="space-y-2">
            <FieldLabel>Image</FieldLabel>
            <Upload
              multiple={false}
              maxCount={1}
              name="avatar"
              onChange={(e) => handleBannerFormChange(e.file, 'image')}
              listType="picture-card"
              className="avatar-uploader"
              beforeUpload={() => false}
            >
              {uploadButton}
            </Upload>
          </div>
          <div className="space-y-2">
            <FieldLabel>Date range</FieldLabel>
            <RangePicker
              style={{ width: '100%' }}
              size="large"
              format="MM/DD/YYYY"
              onChange={(e) => onChangeBannerForm(e)}
            />
          </div>
        </div>
      </DialogShell>
    </div>
  );
};

export default BrandingTab;
